import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import { addTaskMessage } from '../../http/requests/task';
import { toastOptions } from '../../common/utils/toasterOptions';
import './AddTaskMessageModal.css';

const AddTaskMessageModal = ({ open, onClose, task, onMessageAdded }) => {
  const [messageText, setMessageText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!messageText.trim()) {
      toast.error('Please enter a message', toastOptions);
      return;
    }

    // Get the correct task ID - handle both taskId and id fields
    const taskId = task.taskId || task.id;
    
    if (!taskId) {
      console.error('No task ID found:', task);
      toast.error('Error: Task ID not found', toastOptions);
      return;
    }

    console.log('Sending message for task ID:', taskId);
    console.log('Message data:', { messageText: messageText.trim() });

    setSubmitting(true);
    try {
      const messageData = {
        messageText: messageText.trim()
      };

      await addTaskMessage(taskId, messageData);
      
      toast.success('Message added successfully', toastOptions);
      setMessageText('');
      onClose();
      
      // Notify parent component that a message was added
      if (onMessageAdded) {
        onMessageAdded(taskId);
      }
    } catch (error) {
      console.error('Error adding message:', error);
      console.error('Error details:', error.response?.data || error.message);
      toast.error('Failed to add message', toastOptions);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setMessageText('');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      className="add-task-message-modal"
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" component="div">
            Add Message to Task
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {task && (
          <Box mb={2}>
            <Typography variant="subtitle2" color="textSecondary">
              Task: {task.title}
            </Typography>
          </Box>
        )}
        
        <TextField
          autoFocus
          fullWidth
          multiline
          rows={4}
          variant="outlined"
          label="Message"
          placeholder="Enter your message..."
          value={messageText}
          onChange={(e) => setMessageText(e.target.value)}
          disabled={submitting}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px'
            }
          }}
        />
      </DialogContent>

      <DialogActions>
        <Button 
          onClick={handleClose} 
          disabled={submitting}
          variant="outlined"
          sx={{
            color: '#666',
            borderColor: '#999',
            '&:hover': {
              backgroundColor: '#f5f5f5',
              borderColor: '#666'
            }
          }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained"
          disabled={submitting || !messageText.trim()}
          sx={{
            backgroundColor: '#1773bc',
            '&:hover': { backgroundColor: '#125a94' }
          }}
        >
          {submitting ? 'Sending...' : 'Submit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddTaskMessageModal;
